
import { computed, defineComponent, reactive, ref, nextTick, onMounted, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { IgnoreItem, IgnoreService, QueryParams } from './ignore-view-table.service'
import { ElMessage } from 'element-plus'
import {
  PatchInfo,
  VulnerabilityFixService
} from '@/views/dashboard/safe-status/vulnerability-fix/vulnerability-fix.service'
import PatchPopover from '@/views/dashboard/safe-status/vulnerability-fix/_components/patch-popover/patch-popover.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import LoadMoreButton from '@/components/load-more-button.vue'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { resolvedVirusKbDescription } from '@/utils/virus-kb-description'

export default defineComponent({
  components: {
    PatchPopover,
    SearchFilterBar,
    LoadMoreButton
  },
  props: {
    viewMode: {
      type: String as PropType<TableViewModeType>,
      default: TableViewMode.preview
    }
  },
  emits: ['viewAll', 'updatePreview'],
  setup (props, { emit }) {
    const { t, te, locale } = useI18n()
    const tableRef = ref()
    const injector = useReflectiveInjector([IgnoreService, VulnerabilityFixService])
    const service = injector.get(IgnoreService)
    const mainService = injector.get(VulnerabilityFixService)

    const tableData = ref<IgnoreItem[]>([])
    const isLoading = ref(false)
    const hasMore = ref(true)
    const total = ref(0)
    const patch_info = ref<PatchInfo>({} as PatchInfo)
    const selectedItems = ref<IgnoreItem[]>([])

    const queryParams = reactive<QueryParams>({
      page: 1,
      limit: props.viewMode === TableViewMode.preview ? 10 : 20
    })

    const filterFormColumns = reactive({})

    function getData (isLoadMore: boolean = false) {
      if (!isLoadMore) {
        queryParams.page = 1
        tableData.value = []
      }

      isLoading.value = true

      service.getLeakIgnores(queryParams).then(response => {
        if (queryParams.page === 1) {
          tableData.value = response.list
        } else {
          tableData.value = [...tableData.value, ...response.list]
        }
        total.value = response.total
        hasMore.value = tableData.value.length < total.value
        // 清空选中项
        nextTick(() => {
          tableRef.value?.clearSelection()
        })
        selectedItems.value = []
      }).finally(() => {
        isLoading.value = false
      })
    }

    function handleQuery (form: any) {
      Object.assign(queryParams, form)
      queryParams.page = 1
      getData()
    }

    function handleReset () {
      queryParams.page = 1
      getData()
    }

    function loadMoreData () {
      if (!hasMore.value || isLoading.value || props.viewMode === TableViewMode.preview) return
      queryParams.page = queryParams.page! + 1
      getData(true)
    }
    function getPatchInfo (item: IgnoreItem) {
      if (item.patch_info) {
        patch_info.value = item.patch_info
        return
      }
      mainService.getPatchInfo({ kb_number: item.kb_number }).then(response => {
        patch_info.value = response.patch_info
        item.patch_info = response.patch_info
      })
    }

    function displayKbDesc (row: IgnoreItem) {
      void locale.value
      return resolvedVirusKbDescription(row.kb_number, te, t) ?? row.kb_desc ?? t('safeProtectionModule.defaultDesc')
    }

    const selectedCount = computed(() => selectedItems.value.length)

    onMounted(() => {
      getData()
    })

    function handleSelectionChange (selection: IgnoreItem[]) {
      selectedItems.value = selection
    }

    function selectCancel () {
      nextTick(() => {
        tableRef.value?.clearSelection()
      })
      selectedItems.value = []
    }

    function batchCancelIgnore () {
      if (selectedItems.value.length === 0) {
        ElMessage.warning(t('vulnerabilityFixPage.selectLeakCancelIgnoreFirst'))
        return
      }
      const kbNumbers = selectedItems.value.map(item => item.kb_number)
      service.cancelLeakIgnore({ kb_numbers: kbNumbers }).then(() => {
        ElMessage.success(t('vulnerabilityFixPage.batchCancelLeakIgnoreSuccess'))
        selectedItems.value = []
        getData(false)
        if (props.viewMode === TableViewMode.full) {
          emit('updatePreview')
        }
      }).catch(() => {
        ElMessage.error(t('vulnerabilityFixPage.batchCancelLeakIgnoreFail'))
      })
    }

    return {
      tableRef,
      queryParams,
      tableData,
      isLoading,
      total,
      hasMore,
      patch_info,
      selectedItems,
      selectedCount,
      filterFormColumns,
      getPatchInfo,
      displayKbDesc,
      handleQuery,
      handleReset,
      loadMoreData,
      handleSelectionChange,
      selectCancel,
      batchCancelIgnore,
      pageAll(){
        emit('viewAll')
      },
      cancelIgnore (item: IgnoreItem) {
        service.cancelLeakIgnore({ kb_numbers: [item.kb_number] }).then(() => {
          ElMessage.success(t('vulnerabilityFixPage.cancelLeakIgnoreSuccess'))
          if (props.viewMode === TableViewMode.full) {
            const index = tableData.value.findIndex(row => row === item)
            if (index >= 0) {
              tableData.value.splice(index, 1)
              total.value = Math.max(0, total.value - 1)
            }
            emit('updatePreview')
          } else {
            getData()
          }
        }).catch(() => {
          ElMessage.error(t('vulnerabilityFixPage.cancelLeakIgnoreFail'))
        })
      },
      getData,
      TableViewMode
    }
  }
})
